import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import BrandMark from "@/components/BrandMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthGate() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [subscribe, setSubscribe] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/landing/signup-open`)
      .then((r) => { if (!cancelled) setSignupOpen(Boolean(r.data?.public_signup_enabled)); })
      .catch(() => { if (!cancelled) setSignupOpen(false); });
    return () => { cancelled = true; };
  }, []);

  // If signup is closed, force login mode.
  useEffect(() => {
    if (!signupOpen && mode === "register") setMode("login");
  }, [signupOpen, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = mode === "login"
      ? await login(email, password)
      : await register(name, email, password, subscribe);
    setBusy(false);
    if (res.ok) {
      // Routing rules (iter148 — user asked to verify EVERY customer login
      // funnels through the Chooser so they consciously pick a tier):
      //   • admin       → /admin (deep-links honored for admin sections)
      //   • temp_viewer → /select (BBKPREVIEW-style previews land on the story page)
      //   • customer    → /chooser ALWAYS. Deep-link `from` is intentionally
      //                   ignored so a bookmark to /shop/foo doesn't bypass Chooser.
      let dest;
      if (res.user?.role === "admin") {
        dest = loc.state?.from?.pathname || "/admin";
      } else if (res.user?.role === "temp_viewer") {
        dest = "/select";
      } else {
        dest = "/chooser";
      }
      // iter148 — if the customer landed here via a deep-link they'd
      // bookmarked (e.g. /shop/xxx), flag the Chooser so it can nudge
      // them to make a conscious tier pick before continuing.
      const chooserState = (dest === "/chooser" && loc.state?.from?.pathname)
        ? { fromDeepLink: true, intendedPath: loc.state.from.pathname }
        : undefined;
      nav(dest, { replace: true, state: chooserState });
    } else {
      setErr(res.error);
    }
  };

  // iter76: labels ("EMAIL", "PASSWORD") stay small per user; ONLY the typed
  // text is bumped so what the member enters is legible. `text-lg` = ~16px.
  const inputCls = "mt-2 rounded-none border-0 border-b border-charcoal/40 bg-transparent focus-visible:ring-0 focus-visible:border-charcoal px-0 h-12 text-[17px] md:text-[17px]";

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1.1fr] bg-bone">
      {/* Left brand slab */}
      <div className="relative hidden lg:flex flex-col justify-between p-14 bg-charcoal text-bone">
        <BrandMark variant="light" size="lg" asLink={false} />

        <div className="max-w-md">
          <p className="text-[13px] uppercase tracking-[0.28em] text-bone/85 mb-6">Wholesale · Research Grade · Members</p>
          <h1 className="font-serif text-6xl xl:text-7xl leading-[0.95] tracking-tight">
            You're serious<br/>
            about your research.<br/>
            <span className="italic text-bone/85">So are we.</span>
          </h1>
          <div className="h-px bg-bone/20 my-8" />
          <p className="text-lg text-bone/85 leading-relaxed max-w-sm">
            Bulk research kits at wholesale pricing. Members-only access.
          </p>
        </div>

        <p className="text-[12px] font-mono uppercase tracking-[0.25em] text-bone/85">
          Research use only · Not for human consumption
        </p>
      </div>

      {/* Auth form */}
      <div className="flex items-center justify-center px-6 py-16 lg:p-20">
        <div className="w-full max-w-md animate-fade-up">
          <p className="text-[13px] uppercase tracking-[0.28em] text-stone mb-6" data-testid="auth-mode-label">
            {mode === "login" ? "Members · Sign in" : "Members · Register"}
          </p>
          <h2 className="font-serif text-5xl tracking-tight mb-3">
            {mode === "login" ? "Sign in." : "Request access."}
          </h2>
          <p className="text-lg text-stone mb-12">
            {mode === "login"
              ? "The wholesale catalog sits behind a members-only gate."
              : "Register to view kit pricing and place bulk orders."}
          </p>

          <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-[13px] uppercase tracking-[0.22em] text-stone">Full name</Label>
                <Input
                  id="name" value={name} onChange={(e) => setName(e.target.value)} required
                  data-testid="auth-name-input" className={inputCls}
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-[13px] uppercase tracking-[0.22em] text-stone">
                {mode === "login" ? "Email or username" : "Email"}
              </Label>
              <Input
                id="email"
                type={mode === "login" ? "text" : "email"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                data-testid="auth-email-input"
                className={inputCls}
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[13px] uppercase tracking-[0.22em] text-stone">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="auth-password-input"
                className={inputCls}
              />
              {mode === "login" && (
                <div className="mt-2 text-right">
                  <Link
                    to="/forgot-password"
                    className="text-[13px] uppercase tracking-[0.22em] text-stone hover:text-oxblood transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {err && (
              <p className="text-sm text-oxblood" data-testid="auth-error">{err}</p>
            )}

            {mode === "register" && (
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="subscribe-checkbox-label">
                <input
                  type="checkbox"
                  checked={subscribe}
                  onChange={(e) => setSubscribe(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded-none border border-charcoal/60 text-oxblood focus:ring-0 focus:ring-offset-0 accent-oxblood cursor-pointer"
                  data-testid="subscribe-checkbox"
                />
                <span className="text-[13px] text-stone group-hover:text-charcoal transition-colors leading-relaxed">
                  Email me about restocks, new kits, and occasional sales. One-click unsubscribe in every email.
                </span>
              </label>
            )}

            <button
              type="submit" disabled={busy}
              className="w-full py-4 bg-charcoal text-bone tracking-[0.24em] text-xs uppercase hover:bg-oxblood transition-colors disabled:opacity-50 rounded-full"
              data-testid="auth-submit-btn"
            >
              {busy ? "Please wait…" : mode === "login" ? "Enter" : "Create account"}
            </button>
          </form>

          {signupOpen ? (
            <button
              onClick={() => { setErr(""); setMode(mode === "login" ? "register" : "login"); }}
              className="mt-8 inline-flex items-center justify-center px-6 py-3 border-2 border-charcoal bg-bone text-charcoal hover:bg-charcoal hover:text-bone transition-colors font-mono text-[13px] uppercase tracking-[0.22em] font-semibold rounded-full"
              data-testid="auth-toggle-btn"
            >
              {mode === "login" ? "New here? Create an account →" : "Already a member? Sign in →"}
            </button>
          ) : (
            <Link
              to="/#request-access"
              className="mt-8 inline-flex items-center justify-center px-6 py-3 border-2 border-oxblood bg-oxblood text-bone hover:bg-charcoal hover:border-charcoal transition-colors font-mono text-[13px] uppercase tracking-[0.22em] font-semibold rounded-full shadow-sm"
              data-testid="auth-request-access-link"
            >
              New here? Request access →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
